import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ListChecks, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Siren, PencilLine, Pin, Merge } from "lucide-react";
import { EwsDot, fmtTanggal } from "@/lib/siport";
import { SimpleSelect } from "@/components/common";

const emptyForm = { label: "", warna: "#64748B", trigger_code_blue: false };

export default function SiportMaster() {
  const { isSuperAdmin } = useAuth();
  const [kategori, setKategori] = useState({});
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null); // {kategori, id?}
  const [form, setForm] = useState(emptyForm);
  const [manual, setManual] = useState([]); // [{nama, jumlah, terakhir}]
  const [polis, setPolis] = useState([]);
  const [gabung, setGabung] = useState(null); // {nama, jumlah, tujuan}

  const load = () => {
    api.get("/siport/options").then((r) => {
      setKategori(r.data.kategori);
      setItems(r.data.items);
    });
    api.get("/siport/poli-manual").then((r) => setManual(r.data));
    api.get("/siport/form-options").then((r) => setPolis(r.data.poliklinik));
  };
  useEffect(() => { load(); }, []);

  const run = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const openAdd = (k) => {
    setForm(emptyForm);
    setDialog({ kategori: k });
  };
  const openEdit = (o) => {
    setForm({ label: o.label, warna: o.warna || "#64748B", trigger_code_blue: !!o.trigger_code_blue });
    setDialog({ kategori: o.kategori, id: o.id });
  };

  const save = () => {
    if (!form.label.trim()) return toast.error("Label wajib diisi");
    const body = { label: form.label.trim() };
    if (dialog.kategori === "ews_score") body.warna = form.warna;
    if (dialog.kategori === "tindak_lanjut") body.trigger_code_blue = form.trigger_code_blue;
    run(async () => {
      if (dialog.id) await api.put(`/siport/options/${dialog.id}`, body);
      else await api.post("/siport/options", { kategori: dialog.kategori, ...body });
      setDialog(null);
    }, "Opsi disimpan");
  };

  // Tukar urutan dengan tetangga (urutan dinormalisasi ulang 1..n supaya tidak ada nilai kembar)
  const move = (list, idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const reordered = [...list];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    run(() => Promise.all(reordered.map((o, i) => (o.urutan !== i + 1 ? api.put(`/siport/options/${o.id}`, { urutan: i + 1 }) : null))));
  };

  return (
    <div data-testid="siport-master-page">
      <PageHeader icon={ListChecks} title="Master Opsi SIPORT"
        subtitle="Pilihan dropdown di form publik. Opsi yang sudah dipakai laporan tidak bisa dihapus — cukup nonaktifkan." />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {Object.entries(kategori).map(([k, nama]) => {
          const list = items.filter((o) => o.kategori === k);
          return (
            <Card key={k} className="border-slate-200 shadow-sm" data-testid={`siport-master-${k}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-base">{nama}</CardTitle>
                <Button size="sm" variant="outline" onClick={() => openAdd(k)} data-testid={`siport-add-${k}`}>
                  <Plus className="w-4 h-4 mr-1" /> Tambah
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {list.map((o, idx) => (
                    <li key={o.id} className={`flex items-center gap-2 px-3 py-2 ${o.aktif ? "" : "bg-slate-50"}`}>
                      <div className="flex flex-col">
                        <button className="text-slate-400 hover:text-slate-700 disabled:opacity-20" disabled={idx === 0}
                          onClick={() => move(list, idx, -1)} aria-label="Naikkan"><ArrowUp className="w-3.5 h-3.5" /></button>
                        <button className="text-slate-400 hover:text-slate-700 disabled:opacity-20" disabled={idx === list.length - 1}
                          onClick={() => move(list, idx, 1)} aria-label="Turunkan"><ArrowDown className="w-3.5 h-3.5" /></button>
                      </div>
                      {k === "ews_score" && <EwsDot warna={o.warna} />}
                      <div className={`flex-1 min-w-0 text-sm ${o.aktif ? "text-slate-900" : "text-slate-400 line-through"}`}>
                        {o.label}
                        {o.trigger_code_blue && (
                          <Badge className="ml-2 bg-rose-100 text-rose-800 hover:bg-rose-100"><Siren className="w-3 h-3 mr-1" />Code Blue</Badge>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 tabular-nums whitespace-nowrap">{o.dipakai} laporan</span>
                      <Switch checked={o.aktif} data-testid={`siport-toggle-${o.id}`}
                        onCheckedChange={(v) => run(() => api.put(`/siport/options/${o.id}`, { aktif: v }), v ? "Opsi diaktifkan" : "Opsi dinonaktifkan")} />
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(o)}>
                        <Pencil className="w-4 h-4 text-slate-500" />
                      </Button>
                      {isSuperAdmin && o.dipakai === 0 && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8"><Trash2 className="w-4 h-4 text-rose-500" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus opsi “{o.label}”?</AlertDialogTitle>
                              <AlertDialogDescription>Opsi belum dipakai laporan mana pun. Penghapusan bersifat permanen.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction className="bg-rose-600 hover:bg-rose-700"
                                onClick={() => run(() => api.delete(`/siport/options/${o.id}`), "Opsi dihapus")}>Hapus</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </li>
                  ))}
                  {list.length === 0 && <li className="px-3 py-4 text-sm text-slate-400 text-center">Belum ada opsi</li>}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200 shadow-sm mt-6" data-testid="siport-poli-manual">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><PencilLine className="w-4 h-4" />Poli dari isian manual</CardTitle>
          <p className="text-sm text-slate-500">
            Nama poli yang diketik petugas lewat pilihan “Lainnya”. Jadikan pilihan tetap supaya muncul di dropdown berikutnya
            (masuk Poliklinik Tambahan SIPORT, master poliklinik WTRJ/WPRJ tidak berubah), atau gabungkan ke poli yang sudah ada.
          </p>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {manual.map((m) => (
              <li key={m.nama} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <div className="flex-1 min-w-[180px]">
                  <div className="text-sm font-medium text-slate-900">{m.nama}</div>
                  <div className="text-xs text-slate-500">terakhir {fmtTanggal(m.terakhir)}</div>
                </div>
                <span className="text-xs text-slate-500 tabular-nums whitespace-nowrap">{m.jumlah} laporan</span>
                <Button size="sm" variant="outline" data-testid={`siport-poli-tetap-${m.nama}`}
                  onClick={() => run(() => api.post("/siport/poli-manual/jadikan-tetap", { nama: m.nama }), `“${m.nama}” jadi pilihan tetap`)}>
                  <Pin className="w-3.5 h-3.5 mr-1" />Jadikan pilihan tetap
                </Button>
                <Button size="sm" variant="outline" onClick={() => setGabung({ ...m, tujuan: "" })} data-testid={`siport-poli-gabung-${m.nama}`}>
                  <Merge className="w-3.5 h-3.5 mr-1" />Gabungkan ke poli yang ada
                </Button>
              </li>
            ))}
            {manual.length === 0 && <li className="px-3 py-4 text-sm text-slate-400 text-center">Belum ada poli isian manual</li>}
          </ul>
        </CardContent>
      </Card>

      <Dialog open={!!gabung} onOpenChange={(o) => !o && setGabung(null)}>
        <DialogContent data-testid="siport-gabung-dialog">
          <DialogHeader>
            <DialogTitle>Gabungkan “{gabung?.nama}”</DialogTitle>
            <DialogDescription>
              {gabung?.jumlah} laporan akan dipindah ke poli tujuan. Perubahan dicatat di riwayat tiap laporan.
            </DialogDescription>
          </DialogHeader>
          <SimpleSelect value={gabung?.tujuan || undefined} onChange={(v) => setGabung((g) => ({ ...g, tujuan: v }))}
            placeholder="Pilih poli tujuan" className="w-full" testId="siport-gabung-tujuan"
            options={polis.map((p) => ({ label: p.nama, value: p.id }))} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setGabung(null)}>Batal</Button>
            <Button disabled={!gabung?.tujuan} data-testid="siport-gabung-simpan"
              onClick={() => run(async () => {
                const r = await api.post("/siport/poli-manual/gabungkan", { nama: gabung.nama, tujuan_id: gabung.tujuan });
                setGabung(null);
                toast.success(r.data.message);
              })}>Gabungkan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="siport-option-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.id ? "Edit Opsi" : "Tambah Opsi"}</DialogTitle>
            <DialogDescription>{kategori[dialog?.kategori]}. Laporan lama tetap menyimpan label saat laporan dibuat.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Label</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="siport-option-label" />
            </div>
            {dialog?.kategori === "ews_score" && (
              <div className="space-y-1">
                <Label>Warna badge</Label>
                <div className="flex items-center gap-3">
                  <input type="color" value={form.warna} onChange={(e) => setForm({ ...form, warna: e.target.value })}
                    className="h-9 w-14 rounded border border-slate-200" />
                  <span className="text-sm text-slate-500 font-mono">{form.warna}</span>
                </div>
              </div>
            )}
            {dialog?.kategori === "tindak_lanjut" && (
              <label className="flex items-start gap-3 text-sm">
                <Switch checked={form.trigger_code_blue} onCheckedChange={(v) => setForm({ ...form, trigger_code_blue: v })} />
                <span>
                  <b>Memicu isian Code Blue</b>
                  <span className="block text-slate-500">Jika dipilih di form, field alasan, jam aktivasi, jam TMRC, respon time, dan akhir Code Blue wajib diisi.</span>
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Batal</Button>
            <Button onClick={save} data-testid="siport-option-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
